import { useEffect, useState } from 'react';
import { adminServicesApi } from '@api/client';
import type { Room, Service, Staff } from '@api/types';
import * as Icons from '@components/Icons';

interface ServiceFormProps {
  service: Service | null;
  rooms: Room[];
  staff: Staff[];
  /**
   * Render sala-compat sólo si el centro activo tiene
   * `roomsAffectAvailability=true` (si las rooms entran en availability).
   * La sección de staff se renderiza siempre (catálogo global post-0028:
   * la pivot service_staff es la única fuente de verdad).
   */
  showRoomsCompat: boolean;
  onClose: () => void;
  onSave: (
    data: Record<string, unknown>,
    compatibleRoomIds: string[] | null,
    compatibleStaffIds: string[] | null,
  ) => Promise<void>;
  isSaving: boolean;
}

export function ServiceModal({
  service,
  rooms,
  staff,
  showRoomsCompat,
  onClose,
  onSave,
  isSaving,
}: ServiceFormProps) {
  const [name, setName] = useState(service?.name ?? '');
  const [durationMinutes, setDurationMinutes] = useState(String(service?.durationMinutes ?? 60));
  const [active, setActive] = useState(service?.active ?? true);
  // Display price as a string in major units. Empty = not set / free.
  const [priceMajor, setPriceMajor] = useState(
    service?.price === null || service?.price === undefined
      ? ''
      : (service.price / 100).toFixed(2),
  );
  const [category, setCategory] = useState(service?.category ?? '');
  const [description, setDescription] = useState(service?.description ?? '');
  const [color, setColor] = useState(service?.color ?? '');
  const [err, setErr] = useState('');
  const [selectedRoomIds, setSelectedRoomIds] = useState<Set<string>>(new Set());
  const [roomIdsLoaded, setRoomIdsLoaded] = useState(!service);
  const [selectedStaffIds, setSelectedStaffIds] = useState<Set<string>>(new Set());
  const [staffIdsLoaded, setStaffIdsLoaded] = useState(!service);

  useEffect(() => {
    if (!service || !showRoomsCompat) {
      setRoomIdsLoaded(true);
      return;
    }
    let cancelled = false;
    void adminServicesApi.getRooms(service.id).then((resp) => {
      if (cancelled) return;
      setSelectedRoomIds(new Set(resp.roomIds));
      setRoomIdsLoaded(true);
    }).catch(() => {
      if (!cancelled) setRoomIdsLoaded(true);
    });
    return () => { cancelled = true; };
  }, [service, showRoomsCompat]);

  useEffect(() => {
    if (!service) {
      setStaffIdsLoaded(true);
      return;
    }
    let cancelled = false;
    void adminServicesApi.getStaff(service.id).then((resp) => {
      if (cancelled) return;
      setSelectedStaffIds(new Set(resp.staffIds));
      setStaffIdsLoaded(true);
    }).catch(() => {
      if (!cancelled) setStaffIdsLoaded(true);
    });
    return () => { cancelled = true; };
  }, [service]);

  function toggleRoom(roomId: string) {
    setSelectedRoomIds((prev) => {
      const next = new Set(prev);
      if (next.has(roomId)) next.delete(roomId);
      else next.add(roomId);
      return next;
    });
  }

  function toggleStaff(staffId: string) {
    setSelectedStaffIds((prev) => {
      const next = new Set(prev);
      if (next.has(staffId)) next.delete(staffId);
      else next.add(staffId);
      return next;
    });
  }

  async function handleSubmit(e: React.SyntheticEvent) {
    e.preventDefault();
    setErr('');
    if (!name.trim()) { setErr('El nombre es obligatorio.'); return; }
    const dur = parseInt(durationMinutes, 10);
    if (!dur || dur < 1) { setErr('La duración debe ser un número positivo.'); return; }

    // Major units → minor units. Empty string is "not set" (null).
    let priceMinor: number | null = null;
    if (priceMajor.trim().length > 0) {
      const parsed = Number(priceMajor.replace(',', '.'));
      if (!Number.isFinite(parsed) || parsed < 0) {
        setErr('El precio debe ser un número no negativo.');
        return;
      }
      priceMinor = Math.round(parsed * 100);
    }

    const trimmedColor = color.trim();
    if (trimmedColor.length > 0 && !/^#[0-9A-Fa-f]{6}$/.test(trimmedColor)) {
      setErr('El color debe estar en formato #RRGGBB.');
      return;
    }

    const compatibleRoomIds = showRoomsCompat ? Array.from(selectedRoomIds) : null;
    // Catálogo global post-0028: las compatibilidades de staff se gestionan
    // siempre desde el modal — la pivot service_staff es obligatoria para
    // que el servicio aparezca en el widget de algún centro.
    const compatibleStaffIds = Array.from(selectedStaffIds);
    try {
      await onSave({
        name: name.trim(),
        durationMinutes: dur,
        active,
        price: priceMinor,
        category: category.trim().length > 0 ? category.trim() : null,
        description: description.trim().length > 0 ? description.trim() : null,
        color: trimmedColor.length > 0 ? trimmedColor.toUpperCase() : null,
      }, compatibleRoomIds, compatibleStaffIds);
    } catch {
      setErr('Error al guardar. Inténtalo de nuevo.');
    }
  }

  return (
    <div className="modal-scrim" onClick={onClose}>
      <div className="modal" onClick={(e) => { e.stopPropagation(); }}>
        <div className="modal-head">
          <div className="modal-title">{service ? `Editar servicio · ${service.name}` : 'Nuevo servicio'}</div>
          <button className="icon-btn" data-variant="ghost" onClick={onClose}><Icons.X size={14} /></button>
        </div>
        <form className="modal-body" onSubmit={handleSubmit}>
          {err && (
            <div style={{ padding: '10px 14px', background: 'var(--danger-soft)', border: '1px solid oklch(0.85 0.05 25)', borderRadius: 'var(--radius-sm)', fontSize: 13, color: 'var(--accent-dark)', marginBottom: 12 }}>
              {err}
            </div>
          )}
          <div className="field">
            <label className="field-label">Nombre <span style={{ color: 'var(--accent)' }}>*</span></label>
            <input className="input" value={name} onChange={(e) => { setName(e.target.value); }} autoFocus />
          </div>
          <div className="field-row">
            <div className="field">
              <label className="field-label">Duración (min) <span style={{ color: 'var(--accent)' }}>*</span></label>
              <input className="input mono" type="number" min={1} value={durationMinutes} onChange={(e) => { setDurationMinutes(e.target.value); }} />
            </div>
            <div className="field">
              <label className="field-label">Precio</label>
              <input
                className="input mono"
                type="number"
                min={0}
                step="0.01"
                value={priceMajor}
                onChange={(e) => { setPriceMajor(e.target.value); }}
                placeholder="0.00"
              />
              <p style={{ fontSize: 11.5, color: 'var(--fg-soft)', marginTop: 4 }}>
                Déjalo vacío si el servicio es gratuito o no quieres fijarlo aún.
              </p>
            </div>
          </div>
          <div className="field-row">
            <div className="field">
              <label className="field-label">Categoría</label>
              <input
                className="input"
                maxLength={60}
                value={category}
                onChange={(e) => { setCategory(e.target.value); }}
                placeholder="p.ej. Masaje, Estética…"
              />
            </div>
            <div className="field">
              <label className="field-label">Color</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="color"
                  value={color || '#888888'}
                  onChange={(e) => { setColor(e.target.value); }}
                  style={{ width: 36, height: 32, padding: 0, border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', background: 'transparent' }}
                />
                <input
                  className="input mono"
                  value={color}
                  onChange={(e) => { setColor(e.target.value); }}
                  placeholder="#RRGGBB"
                  style={{ flex: 1 }}
                />
                {color && (
                  <button
                    type="button"
                    className="icon-btn"
                    data-variant="ghost"
                    onClick={() => { setColor(''); }}
                    title="Quitar color"
                  >
                    <Icons.X size={12} />
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="field">
            <label className="field-label">Descripción</label>
            <textarea
              className="textarea"
              maxLength={500}
              value={description}
              onChange={(e) => { setDescription(e.target.value); }}
              rows={3}
              placeholder="Texto que verá el cliente en el widget bajo el nombre del servicio."
              style={{ resize: 'vertical', fontSize: 13 }}
            />
          </div>
          <div className="switch-row">
            <div className="switch-row-info">
              <div className="switch-row-name">Servicio activo</div>
              <div className="switch-row-desc">Los servicios inactivos no aparecen en el widget de reservas.</div>
            </div>
            <div className="switch" data-on={active} onClick={() => { setActive((v) => !v); }} />
          </div>
          {showRoomsCompat && (
            <div className="field" style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
              <label className="field-label">Salas compatibles del centro activo</label>
              <p style={{ fontSize: 11.5, color: 'var(--fg-soft)', margin: '0 0 8px' }}>
                Opt-in: si marcas alguna, el servicio sólo podrá hacerse en esas salas (útil para cuellos de botella físicos como “1 sala con ducha”). Si no marcas ninguna, no hay restricción de sala.
              </p>
              {!roomIdsLoaded && (
                <p style={{ fontSize: 12, color: 'var(--fg-muted)' }}>Cargando salas…</p>
              )}
              {roomIdsLoaded && rooms.length === 0 && (
                <p style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
                  Aún no hay salas activas en este centro.
                </p>
              )}
              {roomIdsLoaded && rooms.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {rooms.map((room) => (
                    <label key={room.id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                      <input
                        type="checkbox"
                        checked={selectedRoomIds.has(room.id)}
                        onChange={() => { toggleRoom(room.id); }}
                        style={{ width: 14, height: 14, accentColor: 'var(--accent)' }}
                      />
                      <span>{room.name}</span>
                      {!room.isActive && (
                        <span className="pill" data-tone="neutral" style={{ marginLeft: 4 }}>Inactiva</span>
                      )}
                    </label>
                  ))}
                </div>
              )}
              {roomIdsLoaded && rooms.length > 0 && selectedRoomIds.size === 0 && (
                <p style={{ fontSize: 12, color: 'var(--fg-soft)', marginTop: 6 }}>
                  Sin salas marcadas → cualquier sala libre del centro vale.
                </p>
              )}
            </div>
          )}
          <div className="field" style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
            <label className="field-label">Profesionales asignados</label>
            <p style={{ fontSize: 11.5, color: 'var(--fg-soft)', margin: '0 0 8px' }}>
              Sólo los profesionales marcados podrán atender este servicio. Sin nadie marcado, el servicio no es reservable en ningún centro. Se muestran los del centro activo; cambia de centro para configurar a profesionales de otra clínica.
            </p>
              {!staffIdsLoaded && (
                <p style={{ fontSize: 12, color: 'var(--fg-muted)' }}>Cargando profesionales…</p>
              )}
              {staffIdsLoaded && staff.length === 0 && (
                <p style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
                  Aún no hay profesionales activos en este centro.
                </p>
              )}
              {staffIdsLoaded && staff.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {staff.map((s) => (
                    <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                      <input
                        type="checkbox"
                        checked={selectedStaffIds.has(s.id)}
                        onChange={() => { toggleStaff(s.id); }}
                        style={{ width: 14, height: 14, accentColor: 'var(--accent)' }}
                      />
                      <span>{s.name}</span>
                      {!s.active && (
                        <span className="pill" data-tone="neutral" style={{ marginLeft: 4 }}>Inactivo</span>
                      )}
                    </label>
                  ))}
                </div>
              )}
              {staffIdsLoaded && staff.length > 0 && selectedStaffIds.size === 0 && (
                <p style={{ fontSize: 12, color: 'var(--danger)', marginTop: 6 }}>
                  Sin profesionales marcados → este servicio no será reservable.
                </p>
              )}
            </div>
        </form>
        <div className="modal-foot">
          <button className="btn" onClick={onClose}>Cancelar</button>
          <button className="btn" data-variant="primary" disabled={isSaving} onClick={handleSubmit}>
            {isSaving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}
